import React, { useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import Alert from "../components/Alert";
import { useNavigate } from "react-router-dom";
import "./CardCreator.css";

const Profile = () => {
  const { user, loading: authLoading, updateUser } = useAuth();
  const navigate = useNavigate();
  const [nickname, setNickname] = useState(user?.nickname || "");
  const [language, setLanguage] = useState(user?.language || "en");
  const [defaultShuffle, setDefaultShuffle] = useState(
    user?.default_shuffle_extensions !== undefined ? user.default_shuffle_extensions : true
  );
  const [defaultAllowDuplicates, setDefaultAllowDuplicates] = useState(
    user?.default_allow_duplicate_draws !== undefined ? user.default_allow_duplicate_draws : false
  );
  const [defaultShowUserDecks, setDefaultShowUserDecks] = useState(
    user?.default_show_user_decks !== undefined ? user.default_show_user_decks : false
  );
  const [canShare, setCanShare] = useState(
    user?.can_share !== undefined ? user.can_share : true
  );
  const [showImages, setShowImages] = useState(
    user?.show_images !== undefined ? user.show_images : true
  );
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false); // Form submission loading
  const [dataLoading, setDataLoading] = useState(true); // Initial data fetching
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // Only redirect if we are SURE there's no user, auth is finished, data fetch is finished, AND there's no error to show
    if (!user && !authLoading && !dataLoading && !error) {
      navigate('/login');
    }
  }, [user, authLoading, dataLoading, navigate, error]);

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      setError("");
      try {
        const [langRes, profileRes] = await Promise.all([
          api.get("/api/users/languages"),
          api.get("/api/users/profile")
        ]);

        setLanguages(Array.isArray(langRes.data) ? langRes.data : []);
        // Update local state with fresh profile data
        const userData = profileRes.data.user;
        if (userData) {
          setNickname(userData.nickname || "");
          setLanguage(userData.language || "en");
          setDefaultShuffle(userData.default_shuffle_extensions !== undefined ? userData.default_shuffle_extensions : true);
          setDefaultAllowDuplicates(userData.default_allow_duplicate_draws !== undefined ? userData.default_allow_duplicate_draws : false);
          setDefaultShowUserDecks(userData.default_show_user_decks !== undefined ? userData.default_show_user_decks : false);
          setCanShare(userData.can_share !== undefined ? userData.can_share : true);
          setShowImages(userData.show_images !== undefined ? userData.show_images : true);
          updateUser(userData);
        }
      } catch (err) {
        console.error("Profile fetchData error:", err);
        setError(err.message || "Failed to load profile data");
      } finally {
        setDataLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else if (!authLoading) {
      setDataLoading(false);
    }
  }, [user, authLoading]);

  // Sync local state when user object changes from context (e.g. after login or status check)
  useEffect(() => {
    if (user && !dataLoading) {
      setNickname(user.nickname || "");
      setLanguage(user.language || "en");
      setDefaultShuffle(user?.default_shuffle_extensions !== undefined ? user.default_shuffle_extensions : true);
      setDefaultAllowDuplicates(user?.default_allow_duplicate_draws !== undefined ? user.default_allow_duplicate_draws : false);
      setDefaultShowUserDecks(user?.default_show_user_decks !== undefined ? user.default_show_user_decks : false);
      setCanShare(user?.can_share !== undefined ? user.can_share : true);
      setShowImages(user?.show_images !== undefined ? user.show_images : true);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const res = await api.put("/api/users/profile", {
        nickname: nickname.trim(),
        language,
        default_shuffle_extensions: defaultShuffle,
        default_allow_duplicate_draws: defaultAllowDuplicates,
        default_show_user_decks: defaultShowUserDecks,
        show_images: showImages,
      });
      updateUser(res.data.user);
      setSuccess("Profile updated successfully!");
    } catch (err) {
      setError(err.message || "Failed to update profile");
    }
    setLoading(false);
  };

  const isActuallyLoading = authLoading || dataLoading;

  return (
    <div className="profile-page card-creator-page">
      <div className="creator-section">
        <h1>User profile</h1>
        <Alert type="error" message={error} />
        <Alert type="success" message={success} />

        {isActuallyLoading && !error ? (
          <div className="loading" style={{ marginTop: 24 }}>Loading profile data...</div>
        ) : !error && user ? (
          <>
            <p>Manage your profile here.</p>
            <form onSubmit={handleSubmit} style={{ marginTop: 32 }}>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontWeight: 600 }}>Nickname:</label><br />
                <input
                  type="text"
                  value={nickname}
                  onChange={e => setNickname(e.target.value)}
                  className="card-form-input"
                  maxLength={50}
                  required
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontWeight: 600 }}>Language:</label><br />
                <select
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  className="card-form-input"
                  required
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={defaultShowUserDecks}
                    onChange={e => setDefaultShowUserDecks(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  Show other users' decks
                </label>
                <p>If checked, decks created by other users will be shown in the canvas deck selector.</p>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={defaultShuffle}
                    onChange={e => setDefaultShuffle(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  Shuffle extension decks into the main deck
                </label>
                <p>If checked, extension decks will be shuffled into the main deck when creating a story.<br />
                  If unchecked, extension decks will be added as separate decks after the main deck.</p>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={defaultAllowDuplicates}
                    onChange={e => setDefaultAllowDuplicates(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  Cards can be drawn multiple times
                </label>
                <p>If checked, cards can be drawn multiple times from decks when creating a story.<br />
                  If unchecked, unique cards will be drawn until all cards in a deck are drawn.</p>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={showImages}
                    onChange={e => setShowImages(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  Show background art on cards
                </label>
                <p>If checked, the background art will be shown on cards.<br />
                  If unchecked, the default background will be used on cards.</p>
              </div>
              <button
                type="submit"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </>
        ) : error ? (
          <p style={{ marginTop: 20, color: 'var(--text-secondary)' }}>
            Please resolve the issue above or try refreshing the page.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default Profile; 